import { stripHtml } from "string-strip-html";

const PHRASING_ELEMENTS = [
  "abbr",
  "audio",
  "b",
  "bdi",
  "bdo",
  "br",
  "button",
  "canvas",
  "cite",
  "code",
  "data",
  "datalist",
  "dfn",
  "em",
  "embed",
  "i",
  "iframe",
  "img",
  "input",
  "kbd",
  "label",
  "mark",
  "math",
  "meter",
  "noscript",
  "object",
  "output",
  "picture",
  "progress",
  "q",
  "ruby",
  "s",
  "samp",
  "script",
  "select",
  "slot",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "svg",
  "template",
  "textarea",
  "time",
  "u",
  "var",
  "video",
  "wbr",
  // A few other elements belong to this category, but only if a specific condition is fulfilled:
  "a",    // if it contains only phrasing content
  "area", // if it is a descendant of a <map> element
  "del",  // if it contains only phrasing content
  "ins",  // if it contains only phrasing content
  "link", // if the itemprop attribute is present
  "map",  // if it contains only phrasing content
  "meta"  // if the itemprop attribute is present
];

const getMonthName = (date) =>
  ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][date.getMonth()]

const getDateWithFormat = (date) => `${getMonthName(date)} ${date.getDate()}, ${date.getFullYear()}`

const formatAMPM = (date) => {
  let hours = date.getHours()
  let minutes = date.getMinutes()
  const ampm = hours >= 12 ? 'pm' : 'am'
  hours = hours % 12
  hours = hours || 12
  minutes = minutes < 10 ? '0' + minutes : minutes
  const strTime = hours + ':' + minutes + ' ' + ampm
  return strTime
}

/**
 * Appends format=webp to the provided URL string.
 * 
 * @param {string | null | undefined} url
 * @returns {string} The URL string with format=webp added to the query string; returns an empty string by default.
 */
const addWebPToImageURL = (url) => {
  if (!url) return '';
  return url + (url.includes("?") ? "&format=webp" : "?format=webp");
}

function isPropertyNullOrEmpty(property) {
  return property === null || property === undefined || property === ''
}

/**
 * Removes HTML from the provided input string.
 * 
 * @param {string | null | undefined} htmlString
 * @returns {string | null | undefined} Null or undefined if input string is null or undefined, otherwise non-null string value.
 */
const stripHTML = (htmlString) => {
  if (htmlString == null) {
    return htmlString;
  }
  return stripHtml(htmlString)?.result || '';
}

/**
 * Removes HTML from the provided input string, including all content contained within the removed HTML.
 * 
 * @param {string | null | undefined} htmlString
 * @returns {string | null | undefined} Null or undefined if input string is null or undefined, otherwise non-null string value.
 */
const stripHTMLContents = (htmlString) => {
  if (htmlString == null) {
    return htmlString;
  }
  return stripHtml(htmlString, {
    stripTogetherWithTheirContents: ["*"]
  })?.result || '';
}

/**
 * Removes HTML from the provided input string, ignoring "Phrasing Content" elements:
 * 
 * Phrasing content, a subset of flow content, refers to the text and the markup within a document. 
 * 
 * References:
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Guides/Content_categories#phrasing_content
 * https://html.spec.whatwg.org/multipage/dom.html#phrasing-content-2
 * 
 * @param {string | null | undefined} htmlString
 * @returns {string | null | undefined} Null or undefined if input string is null or undefined, otherwise non-null string value.
 */
const stripHTMLNonPhrasing = (htmlString) => {
  if (htmlString == null) {
    return htmlString;
  }
  return stripHtml(htmlString, {
    ignoreTags: PHRASING_ELEMENTS
  })?.result || '';
}

export {
  formatAMPM,
  getMonthName,
  getDateWithFormat,
  addWebPToImageURL,
  isPropertyNullOrEmpty,
  stripHTML,
  stripHTMLContents,
  stripHTMLNonPhrasing
}

